import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import {
	getConnectionStatus,
	subscribeConnectionStatus,
} from "contexts/connectionStatus";

/**
 * Tells someone already working in the app that the backend has gone away.
 *
 * A banner rather than a full page on purpose: replacing a loaded scene editor
 * with an error screen discards unsaved work at exactly the moment it cannot be
 * saved. The app stays mounted and usable; only the warning is new.
 */
const ConnectionBanner: React.FC = () => {
	const { t } = useTranslation();

	const status = React.useSyncExternalStore(
		subscribeConnectionStatus,
		getConnectionStatus,
		getConnectionStatus,
	);

	const [browserOffline, setBrowserOffline] = React.useState(
		() => typeof navigator !== "undefined" && navigator.onLine === false,
	);

	React.useEffect(() => {
		const goOffline = () => setBrowserOffline(true);
		const goOnline = () => setBrowserOffline(false);

		window.addEventListener("offline", goOffline);
		window.addEventListener("online", goOnline);

		return () => {
			window.removeEventListener("offline", goOffline);
			window.removeEventListener("online", goOnline);
		};
	}, []);

	if (status === "online" && !browserOffline) {
		return null;
	}

	// Worth distinguishing: one of these the user can fix, the other they can't.
	const message = browserOffline
		? String(t("app.connection.offline"))
		: String(t("app.connection.degraded"));

	return (
		<Banner role="status" aria-live="polite">
			{message}
		</Banner>
	);
};

const Banner = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 2000;
	padding: 10px var(--gutter);
	text-align: center;
	font-family: var(--font-family);
	font-size: var(--font-size-caption);
	color: var(--color-text);
	background-color: var(--color-warning-bg);
	border-bottom: var(--border-default);
`;

export default ConnectionBanner;
